import { useCallback, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, CircularProgress, IconButton, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import dayjs, { Dayjs } from 'dayjs';
import { useForm } from 'react-hook-form';

import { schema } from './schema';
import { ControlledDateTimeField } from '../../../../components/FormControlsV2/ControlledDateTimeField';
import { FormProvider } from '../../../../components/FormControlsV2/FormProvider';
import { useClientIirExport } from '../queries';

type FormFields = {
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
};

type Props = {
  agreementId: string;
  onClose: () => void;
};

const getApiErrorMessage = (error: unknown) => {
  if (!(error instanceof Error)) {
    return 'Не удалось выгрузить ИИР. Попробуйте повторить запрос.';
  }

  try {
    const parsedError = JSON.parse(error.message) as { message?: string };
    return parsedError.message || 'Не удалось выгрузить ИИР. Попробуйте повторить запрос.';
  } catch {
    return error.message || 'Не удалось выгрузить ИИР. Попробуйте повторить запрос.';
  }
};

export const ClientIirExportDialog = ({ agreementId, onClose }: Props) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const { mutateAsync: exportIirs, isPending: isLoading } = useClientIirExport();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      dateFrom: null,
      dateTo: null,
    },
    resolver: yupResolver(schema),
  });

  const handleReset = useCallback(() => {
    reset();
    setApiError(null);
  }, [reset]);

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  const onSubmit = handleSubmit(async (values) => {
    setApiError(null);

    try {
      const result = await exportIirs({
        agreementId,
        dateFrom: dayjs(values.dateFrom).format('YYYY-MM-DD'),
        dateTo: dayjs(values.dateTo).format('YYYY-MM-DD'),
      });

      if (result && 'success' in result && !result.success) {
        setApiError(result.errorMessage || 'Не удалось выгрузить ИИР.');
        return;
      }

      onClose();
    } catch (error) {
      setApiError(getApiErrorMessage(error));
    }
  });

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={isLoading ? undefined : onClose}>
      <DialogTitle align="center">Выгрузка ИИР</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        disabled={isLoading}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <form id="client-iir-export-form" onSubmit={onSubmit}>
          <FormProvider control={control} errors={errors}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <ControlledDateTimeField
                name="dateFrom"
                label="Дата начала"
                format="DD.MM.YYYY"
                maxDate={dateTo ? dateTo.toDate() : new Date()}
              />
              <ControlledDateTimeField
                name="dateTo"
                label="Дата окончания"
                format="DD.MM.YYYY"
                minDate={dateFrom ? dateFrom.toDate() : undefined}
                maxDate={new Date()}
              />
            </Stack>
          </FormProvider>
        </form>

        {apiError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setApiError(null)}>
            {apiError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} disabled={isLoading}>
          Сбросить
        </Button>
        <Button onClick={onClose} disabled={isLoading}>
          Отмена
        </Button>
        <Button
          type="submit"
          form="client-iir-export-form"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          Выгрузить
        </Button>
      </DialogActions>
    </Dialog>
  );
};
